import { InAppReview } from "@capacitor-community/in-app-review";
import moment from "moment";
import { MatchPhone } from "refsix-js-models";
import { hasGoodQualityFitnessData } from "refsix-core";
import { isAndroid, isIos } from "./platformDetection";
import { trackEvent } from "./analytics/analyticsService";
import { updateUserActionsCheckList } from "./userActionsCheckListService";

/**
 * How long (in months) we wait before showing the rating & review prompt again.
 * The prompt is shown at most once per this window per user.
 */
export const RATE_PROMPT_INTERVAL_MONTHS = 4;

/**
 * How many of the user's most recent results must be good quality before we
 * consider them a happy, engaged user worth prompting for a review.
 */
export const RECENT_GOOD_RESULTS_REQUIRED = 3;

/**
 * Minimum number of finished matches (results) the user must have before we
 * surface the prompt — avoids prompting brand-new users.
 */
export const MIN_RESULTS_REQUIRED = 3;

/**
 * Decides whether the rating & review prompt is eligible to be shown. The caller
 * is still responsible for the "dwell time" (≥10s on the result page) before
 * actually triggering it.
 *
 * Conditions:
 *  - Native platform only (iOS / Android, not mobileweb)
 *  - The match currently being viewed is finished and has good-quality GPS + HR
 *    data (see core helper)
 *  - The user has more than MIN_RESULTS_REQUIRED finished matches
 *  - Their RECENT_GOOD_RESULTS_REQUIRED most recent results are all good quality
 *  - The prompt has not been shown within the last RATE_PROMPT_INTERVAL_MONTHS
 */
export function shouldShowRatingPrompt(
    match: MatchPhone,
    results: MatchPhone[],
    ratePromptLastShown?: number
): boolean {
    if (!isIos() && !isAndroid()) {
        return false;
    }

    if (!match.matchFinished || !hasGoodQualityFitnessData(match)) {
        return false;
    }

    if (results.length <= MIN_RESULTS_REQUIRED) {
        return false;
    }

    // Sort most-recent-first defensively so we don't depend on the caller's
    // ordering, then check the most recent results are all good quality.
    const recentResults = [...results]
        .sort(
            (a, b) =>
                new Date(b.date as string).getTime() -
                new Date(a.date as string).getTime()
        )
        .slice(0, RECENT_GOOD_RESULTS_REQUIRED);

    if (!recentResults.every(hasGoodQualityFitnessData)) {
        return false;
    }

    if (ratePromptLastShown) {
        const recentlyShown = moment(ratePromptLastShown).isAfter(
            moment().subtract(RATE_PROMPT_INTERVAL_MONTHS, "months")
        );
        if (recentlyShown) {
            return false;
        }
    }

    return true;
}

/**
 * Requests the native in-app review flow (SKStoreReviewController on iOS,
 * Play In-App Review on Android). Best-effort: the OS may choose not to show
 * the sheet and there is no callback indicating whether a review was left, so
 * we never block UX on it.
 */
export async function requestInAppReview(): Promise<void> {
    try {
        await InAppReview.requestReview();
    } catch (err) {
        console.warn("ratingPrompt requestReview failed", err);
    }
}

/**
 * Fires the rating prompt: records that the user was prompted (the
 * `reviewRequested` Mixpanel event + the 4-month cooldown timestamp) and
 * surfaces the native app-store review sheet.
 *
 * @param recordShown when false (dev/QA triggers) we skip the analytics event
 *   and cooldown update so they don't pollute metrics or burn the window.
 */
export async function triggerRatingPrompt(recordShown = true): Promise<void> {
    if (recordShown) {
        trackEvent("reviewRequested", {});
        updateUserActionsCheckList({ ratePromptLastShown: Date.now() });
    }
    await requestInAppReview();
}
